import React, { useState, useEffect, useRef } from 'react';
import { IconButton } from '@atlaskit/button/new';
import Icon from '@atlaskit/icon';
import HipchatOutgoingSoundIcon from '@atlaskit/icon/glyph/hipchat/outgoing-sound';
import VidPauseIcon from '@atlaskit/icon/glyph/vid-pause';
import VidPlayIcon from '@atlaskit/icon/glyph/vid-play';
import VidVolumeMutedIcon from '@atlaskit/icon/glyph/vid-volume-muted';
import { Inline, Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { useStandupTimerStore } from '../../controllers/timer-store';
import { StopIcon } from './assets/StopIcon';
import messages from './messages';
import TimerInput from './timer-input';

export const TIMER_TEST_ID = 'rituals.standups.ui.timer';

const Timer = () => {
	const [isSoundOn, setIsSoundOn] = useState(true);
	const [
		{ elapsedSeconds, started },
		{ startTimer, pauseTimer, stopTimer, incrementElapsedSeconds },
	] = useStandupTimerStore();

	const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

	const { formatMessage } = useIntl();

	// Once the timer has started, increment elapsed seconds every second to keep the timer running
	useEffect(() => {
		if (started) {
			intervalRef.current = setInterval(() => {
				incrementElapsedSeconds();
			}, 1000);
		} else if (intervalRef.current) {
			clearInterval(intervalRef.current);
			intervalRef.current = null;
		}

		// Cleanup function to clear the interval if the component unmounts or `started` changes
		return () => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
				intervalRef.current = null;
			}
		};
	}, [started, incrementElapsedSeconds]);

	return (
		<Inline xcss={inlineTimerStyles} testId={TIMER_TEST_ID}>
			<TimerInput />
			{started ? (
				<IconButton
					onClick={() => pauseTimer({ elapsed: elapsedSeconds })}
					icon={(iconProps) => <VidPauseIcon {...iconProps} />}
					label={formatMessage(messages.pauseNonfinal)}
					shape="circle"
				/>
			) : (
				<IconButton
					onClick={() => startTimer()}
					icon={(iconProps) => <VidPlayIcon {...iconProps} />}
					label={formatMessage(messages.playNonfinal)}
					shape="circle"
				/>
			)}
			{/* TODO: Replace stop icon with ADS icon once https://product-fabric.atlassian.net/browse/DSP-20528 is finalised */}
			<IconButton
				icon={(iconProps) => <Icon {...iconProps} glyph={StopIcon} />}
				label={formatMessage(messages.stopNonfinal)}
				onClick={() => stopTimer()}
				shape="circle"
			/>
			<Box xcss={plusOneMinBoxStyles} paddingInlineEnd="space.100">
				{isSoundOn ? (
					<IconButton
						appearance="subtle"
						onClick={() => setIsSoundOn(false)}
						icon={(iconProps) => <HipchatOutgoingSoundIcon {...iconProps} />}
						label={formatMessage(messages.soundOnNonfinal)}
						shape="circle"
					/>
				) : (
					<IconButton
						appearance="subtle"
						onClick={() => setIsSoundOn(true)}
						icon={(iconProps) => <VidVolumeMutedIcon {...iconProps} />}
						label={formatMessage(messages.soundOffNonfinal)}
						shape="circle"
					/>
				)}
			</Box>
		</Inline>
	);
};

export default Timer;

const inlineTimerStyles = xcss({
	width: '245px',
	height: '54px',
	border: `2px solid ${token('color.border')}`,
	borderRadius: '8px',
	alignItems: 'center',
	paddingLeft: 'space.150',
});

const plusOneMinBoxStyles = xcss({
	marginLeft: 'auto',
});
